import * as setup from '@/setup';
import Button from '@/objects/button';
import Header from '@/objects/header';
import axios from 'axios';
import { createTableBackground } from '@/gui/game-over-scene';
import { getUser, getGameModeId } from '@/utils/auth';
import runtime from '@/runtime';
import * as audio from '@/utils/audio';
import { HEADER_HEIGHT } from '@/constants';

export default class Challenge extends Phaser.Scene {
	constructor() {
		super({key: 'Challenge'});
		this.sentChallenges = [];
		this.receivedChallenges = [];
		this.loadingText = null;
		this.currentUserNickname = 'Tu';
		// Proprietà per lo scroll
		this.scrollContainer = null;
		this.scrollMask = null;
		this.scrollY = 0;
		this.minScrollY = 0;
		this.maxScrollY = 0;
		this.isDragging = false;
		this.dragStartY = 0;
		this.scrollStartY = 0;
	}

	create() {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Ottieni nickname utente corrente
		var currentUser = getUser();
		if (currentUser && currentUser.nickname) {
			this.currentUserNickname = currentUser.nickname;
		}

		// Sfondo del gioco (stesso del Game scene)
		this.add
			.image(centerX, height / 2, 'background')
			.setOrigin(0.5, 0.5);

		// Header standard
		this.createHeader(centerX);

		// Carica le challenge
		this.loadChallenges();
	}

	createHeader(centerX) {
		const headerY = HEADER_HEIGHT / 2;

		// Usa il componente Header
		new Header(this, 'SFIDE');

		// Pulsante back (icona) - separato dal componente Header
		const backArrow = this.add.image(40, headerY, 'back-icon');
		backArrow.setOrigin(0, 0.5);
		backArrow.setInteractive({ useHandCursor: true });

		backArrow.on('pointerup', () => {
			audio.playSound(this, 'button-click');
			this.scene.start('MainMenu');
		});
	}


	loadChallenges() {
		const centerX = this.sys.game.config.width / 2;
		const centerY = this.sys.game.config.height / 2;

		// Testo di caricamento
		this.loadingText = this.add.text(
			centerX,
			centerY,
			'Caricamento sfide...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		// Carica sfide inviate e ricevute in parallelo
		Promise.all([
			axios.get('/api/invites/sent'),
			axios.get('/api/invites')
		]).then(([sentResponse, receivedResponse]) => {
			this.loadingText.destroy();
			this.sentChallenges = sentResponse.data || [];
			this.receivedChallenges = receivedResponse.data || [];

			// Debug: verifica se il round è incluso
			console.log('Sfide inviate:', this.sentChallenges);
			if (this.sentChallenges.length > 0) {
				console.log('Prima sfida inviata:', this.sentChallenges[0]);
				console.log('Round della prima sfida:', this.sentChallenges[0].round);
			}

			// Filtra le challenge per stato
			this.displayChallenges();
		}).catch((error) => {
			console.error('Errore caricamento sfide:', error);
			this.loadingText.destroy();
			this.showError('Errore nel caricamento delle sfide');
		});
	}

	displayChallenges() {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Costanti status
		const STATUS_WAITING = 10;
		const STATUS_ACCEPTED = 20;
		const ROUND_STATUS_CLOSED = 30;

		// Funzione helper per ordinare per data (più recente prima)
		const sortByDate = function(a, b) {
			const dateA = parseInt(a.created_at) || 0;
			const dateB = parseInt(b.created_at) || 0;
			return dateB - dateA;
		};

		// Debug: verifica i dati ricevuti
		console.log('Totale sfide inviate:', this.sentChallenges.length);
		console.log('Totale sfide ricevute:', this.receivedChallenges.length);
		if (this.sentChallenges.length > 0) {
			console.log('Status prima sfida inviata:', this.sentChallenges[0].status, 'tipo:', typeof this.sentChallenges[0].status);
			console.log('Challenged round prima sfida:', this.sentChallenges[0].challenged_round);
		}

		// Helper per verificare se entrambi i round sono chiusi
		const isBothRoundsClosed = function(c) {
			const status = parseInt(c.status);
			if (status !== STATUS_ACCEPTED) return false;
			if (!c.round || !c.challenged_round) return false;
			const roundStatus = parseInt(c.round.status);
			const challengedRoundStatus = parseInt(c.challenged_round.status);
			return roundStatus === ROUND_STATUS_CLOSED && challengedRoundStatus === ROUND_STATUS_CLOSED;
		};

		// === SFIDE INVIATE ===
		// "IL LORO TURNO!" - Sfide inviate in attesa che l'amico accetti o giochi
		const theirTurnSentChallenges = this.sentChallenges.filter(function(c) {
			const status = parseInt(c.status);
			// Status WAITING = aspetto che l'amico accetti
			if (status === STATUS_WAITING) return true;
			// Status ACCEPTED ma l'amico non ha ancora completato
			if (status === STATUS_ACCEPTED) {
				if (!c.challenged_round) return true;
				var challengedRoundStatus = parseInt(c.challenged_round.status);
				return challengedRoundStatus !== ROUND_STATUS_CLOSED;
			}
			return false;
		});

		// Le sfide inviate non vanno in "IL TUO TURNO" (hai già giocato)
		const yourTurnSentChallenges = [];

		// "SFIDE CONCLUSE" - Entrambi hanno completato
		const completedSentChallenges = this.sentChallenges.filter(isBothRoundsClosed);

		// === SFIDE RICEVUTE ===
		// "IL TUO TURNO!" - Sfide che devo accettare o a cui devo giocare
		const yourTurnReceivedChallenges = this.receivedChallenges.filter(function(c) {
			const status = parseInt(c.status);
			// Status WAITING = devo accettare
			if (status === STATUS_WAITING) return true;
			// Status ACCEPTED ma non ho ancora completato il mio round
			if (status === STATUS_ACCEPTED) {
				if (!c.challenged_round) return true;
				var challengedRoundStatus = parseInt(c.challenged_round.status);
				return challengedRoundStatus !== ROUND_STATUS_CLOSED;
			}
			return false;
		});

		// Le sfide ricevute non vanno in "IL LORO TURNO" (l'amico ha già giocato quando te le ha inviate)
		const theirTurnReceivedChallenges = [];

		// "SFIDE CONCLUSE" - Entrambi hanno completato
		const completedReceivedChallenges = this.receivedChallenges.filter(isBothRoundsClosed);

		// Aggiungi flag per distinguere sfide inviate da ricevute
		yourTurnSentChallenges.forEach(function(c) { c._isSent = true; });
		theirTurnSentChallenges.forEach(function(c) { c._isSent = true; });
		yourTurnReceivedChallenges.forEach(function(c) { c._isSent = false; });
		theirTurnReceivedChallenges.forEach(function(c) { c._isSent = false; });
		completedSentChallenges.forEach(function(c) { c._isSent = true; });
		completedReceivedChallenges.forEach(function(c) { c._isSent = false; });

		// Combina e ordina le sfide per sezione (senza limiti per permettere lo scroll)
		const yourTurnChallenges = [].concat(yourTurnSentChallenges, yourTurnReceivedChallenges).sort(sortByDate);
		const theirTurnChallenges = [].concat(theirTurnSentChallenges, theirTurnReceivedChallenges).sort(sortByDate);
		const completedChallenges = [].concat(completedSentChallenges, completedReceivedChallenges).sort(sortByDate);

		console.log('=== DEBUG FILTRI ===');
		console.log('yourTurnSentChallenges:', yourTurnSentChallenges.length);
		console.log('yourTurnReceivedChallenges:', yourTurnReceivedChallenges.length);
		console.log('theirTurnSentChallenges:', theirTurnSentChallenges.length);
		console.log('theirTurnReceivedChallenges:', theirTurnReceivedChallenges.length);
		console.log('completedSentChallenges:', completedSentChallenges.length);
		console.log('completedReceivedChallenges:', completedReceivedChallenges.length);
		console.log('=== TOTALI ===');
		console.log('Sfide filtrate - IL TUO TURNO:', yourTurnChallenges.length);
		console.log('Sfide filtrate - IL LORO TURNO:', theirTurnChallenges.length);
		console.log('Sfide filtrate - CONCLUSE:', completedChallenges.length);

		// === AREA SCROLLABILE ===
		const scrollAreaTop = HEADER_HEIGHT + 30;
		const scrollAreaBottom = height - 20;
		const scrollAreaHeight = scrollAreaBottom - scrollAreaTop;

		// Crea il container scrollabile
		this.scrollContainer = this.add.container(0, scrollAreaTop);

		// Crea la maschera per nascondere il contenuto fuori dall'area visibile
		this.scrollMask = this.make.graphics();
		this.scrollMask.fillStyle(0xffffff);
		this.scrollMask.fillRect(0, scrollAreaTop, width, scrollAreaHeight);

		const mask = this.scrollMask.createGeometryMask();
		this.scrollContainer.setMask(mask);

		// Posizione Y relativa all'interno del container (inizia da 0)
		let currentY = 25;

		if (yourTurnChallenges.length > 0) {
			currentY = this.createSection(
				centerX,
				currentY,
				'IL TUO TURNO!',
				'blue-box',
				yourTurnChallenges,
				true
			);
		}
		if (theirTurnChallenges.length > 0) {
			currentY = this.createSection(
				centerX,
				currentY,
				'IL LORO TURNO!',
				'orange-box',
				theirTurnChallenges,
				false
			);
		}
		if (completedChallenges.length > 0) {
			currentY = this.createSection(
				centerX,
				currentY,
				'SFIDE CONCLUSE',
				'gray-box',
				completedChallenges,
				false
			);
		}

		// Se non ci sono challenge, mostra un messaggio
		if (yourTurnChallenges.length === 0 && theirTurnChallenges.length === 0 && completedChallenges.length === 0) {
			const noChallText = this.add.text(
				centerX,
				currentY,
				'NESSUNA SFIDA IN CORSO',
				{...setup.styles.gameOver.greetingStyle, fontSize: 28, fill: '#666666'}
			).setOrigin(0.5);
			this.scrollContainer.add(noChallText);
			currentY += 50;
		}

		// Calcola i limiti dello scroll
		const contentHeight = currentY + 20;
		this.maxScrollY = Math.max(0, contentHeight - scrollAreaHeight);
		this.scrollY = 0;

		// Configura l'input per lo scroll
		this.setupScrollInput(scrollAreaTop, scrollAreaBottom, width);
	}

	/**
	 * Configura l'input per lo scroll con drag/touch e rotella del mouse
	 * @param {number} scrollAreaTop Posizione Y superiore dell'area scrollabile
	 * @param {number} scrollAreaBottom Posizione Y inferiore dell'area scrollabile
	 * @param {number} width Larghezza dell'area scrollabile
	 */
	setupScrollInput(scrollAreaTop, scrollAreaBottom, width) {
		var self = this;

		// Crea una zona interattiva trasparente per catturare gli eventi di drag
		// Depth molto basso per non interferire con gli elementi cliccabili delle card
		var hitArea = this.add.rectangle(
			width / 2,
			(scrollAreaTop + scrollAreaBottom) / 2,
			width,
			scrollAreaBottom - scrollAreaTop,
			0x000000,
			0
		);
		hitArea.setDepth(-10);
		hitArea.setInteractive({ draggable: true });

		// Gestione del drag per lo scroll
		this.input.on('dragstart', function(pointer) {
			self.isDragging = false;
			self.dragStartY = pointer.y;
			self.scrollStartY = self.scrollY;
		});

		this.input.on('drag', function(pointer) {
			var deltaY = pointer.y - self.dragStartY;

			// Se il movimento è maggiore di una soglia, attiva isDragging
			if (Math.abs(deltaY) > 5) {
				self.isDragging = true;
			}

			// Calcola la nuova posizione di scroll
			var newScrollY = self.scrollStartY - deltaY;

			// Limita lo scroll ai bounds
			newScrollY = Math.max(self.minScrollY, Math.min(self.maxScrollY, newScrollY));

			self.scrollY = newScrollY;
			self.scrollContainer.y = scrollAreaTop - self.scrollY;
		});

		this.input.on('dragend', function() {
			// Reset isDragging dopo un breve delay per permettere il click
			self.time.delayedCall(50, function() {
				self.isDragging = false;
			});
		});

		// Gestione della rotella del mouse
		this.input.on('wheel', function(pointer, gameObjects, deltaX, deltaY) {
			// deltaY è positivo quando si scrolla verso il basso
			var scrollAmount = deltaY * 0.5; // Regola la velocità dello scroll

			var newScrollY = self.scrollY + scrollAmount;

			// Limita lo scroll ai bounds
			newScrollY = Math.max(self.minScrollY, Math.min(self.maxScrollY, newScrollY));

			self.scrollY = newScrollY;
			self.scrollContainer.y = scrollAreaTop - self.scrollY;
		});
	}

	createSection(centerX, startY, title, boxImage, challenges, isYourTurn) {
		const width = this.sys.game.config.width;
		const headerHeight = 50;
		const cardSpacing = 90;
		const cardHeight = 80;

		// Header della sezione usando grafica invece di PNG
		// Determina il colore in base a boxImage (blue-box = blu, orange-box = arancione, gray-box = grigio)
		const isBlue = boxImage === 'blue-box';
		const isGray = boxImage === 'gray-box';
		var headerColor;
		if (isBlue) {
			headerColor = 0x2C6B9B;
		} else if (isGray) {
			headerColor = 0x808080;
		} else {
			headerColor = 0xFF8C42;
		}
		const tableWidth = width * 0.9;
		const cornerRadius = 10;

		// Crea l'ombra per l'header (solo angoli superiori arrotondati)
		const shadowGraphics = this.add.graphics();
		shadowGraphics.fillStyle(0xD0D0D0, 0.3);
		// Disegna rettangolo arrotondato e poi copri la parte inferiore
		shadowGraphics.fillRoundedRect(
			centerX - tableWidth / 2 + 2,
			startY - headerHeight / 2 + 2,
			tableWidth,
			headerHeight,
			cornerRadius
		);
		// Copri gli angoli inferiori per renderli quadrati
		shadowGraphics.fillRect(
			centerX - tableWidth / 2 + 2,
			startY - headerHeight / 2 + 2 + headerHeight - cornerRadius,
			tableWidth,
			cornerRadius
		);
		this.scrollContainer.add(shadowGraphics);

		// Crea l'header con il colore appropriato (solo angoli superiori arrotondati)
		const headerGraphics = this.add.graphics();
		headerGraphics.fillStyle(headerColor, 1);
		// Disegna rettangolo arrotondato
		headerGraphics.fillRoundedRect(
			centerX - tableWidth / 2,
			startY - headerHeight / 2,
			tableWidth,
			headerHeight,
			cornerRadius
		);
		// Copri gli angoli inferiori per renderli quadrati
		headerGraphics.fillRect(
			centerX - tableWidth / 2,
			startY - headerHeight / 2 + headerHeight - cornerRadius,
			tableWidth,
			cornerRadius
		);
		this.scrollContainer.add(headerGraphics);

		// Aggiungi un leggero highlight per effetto glossy (solo bordo superiore)
		const highlightGraphics = this.add.graphics();
		var highlightColor;
		if (isBlue) {
			highlightColor = 0x4A8FC7;
		} else if (isGray) {
			highlightColor = 0xA0A0A0;
		} else {
			highlightColor = 0xFFB366;
		}
		// Disegna solo il bordo superiore arrotondato
		highlightGraphics.lineStyle(2, highlightColor, 0.3);
		// Bordo superiore sinistro
		highlightGraphics.beginPath();
		highlightGraphics.moveTo(centerX - tableWidth / 2 + cornerRadius, startY - headerHeight / 2 + 1);
		highlightGraphics.lineTo(centerX + tableWidth / 2 - cornerRadius, startY - headerHeight / 2 + 1);
		// Arco superiore destro
		highlightGraphics.arc(centerX + tableWidth / 2 - cornerRadius, startY - headerHeight / 2 + 1 + cornerRadius, cornerRadius, -Math.PI / 2, 0, false);
		highlightGraphics.strokePath();
		// Arco superiore sinistro
		highlightGraphics.beginPath();
		highlightGraphics.arc(centerX - tableWidth / 2 + cornerRadius, startY - headerHeight / 2 + 1 + cornerRadius, cornerRadius, Math.PI, Math.PI / 2, true);
		highlightGraphics.strokePath();
		this.scrollContainer.add(highlightGraphics);

		// Testo del titolo passato come parametro
		// Per "SFIDE CONCLUSE" riduci Y di 5 pixel
		var titleY = isGray ? startY - 5 : startY;
		const headerText = this.add.text(centerX, titleY, title, {
			fontSize: 28,
			fontFamily: 'Luckiest Guy',
			fill: '#FFFFFF',
			fontStyle: 'bold'
		}).setOrigin(0.5);
		this.scrollContainer.add(headerText);

		let currentY = startY + headerHeight + 15;

		// Determina se è la sezione sfide concluse
		const isCompletedSection = (title === 'SFIDE CONCLUSE');

		// Lista challenge (senza limite di altezza, scrollabile)
		challenges.forEach((challenge, index) => {
			// L'ultima riga ha il bordo inferiore arrotondato
			const isLastRow = index === challenges.length - 1;
			// Usa il flag _isSent per determinare se è una sfida inviata
			const isSent = challenge._isSent !== undefined ? challenge._isSent : false;

			if (isCompletedSection) {
				// Usa layout speciale per sfide concluse
				currentY = this.createCompletedChallengeCard(
					centerX,
					currentY,
					challenge,
					isLastRow,
					isSent
				);
			} else {
				currentY = this.createChallengeCard(
					centerX,
					currentY,
					challenge,
					isYourTurn,
					isLastRow,
					isSent
				);
			}
			currentY += 1; // Spaziatura tra le card
		});

		return currentY;
	}

	createChallengeCard(centerX, y, challenge, isYourTurn, isLastRow = false, isSentChallenge = false) {
		const cardWidth = this.sys.game.config.width * 0.9;
		const cardHeight = 80;
		const padding = 5;
		const borderRadius = 10;

		// Background card
		const cardGraphics = this.add.graphics();
		cardGraphics.fillStyle(0xFFFFFF, 1);

		if (isLastRow) {
			// Ultima riga: bordo inferiore arrotondato
			// Disegna rettangolo arrotondato
			cardGraphics.fillRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
			// Copri gli angoli superiori per renderli quadrati
			cardGraphics.fillRect(
				centerX - cardWidth / 2,
				y - cardHeight / 2,
				cardWidth,
				borderRadius
			);
			// Bordo con angoli arrotondati solo in basso
			cardGraphics.lineStyle(1, 0xE0E0E0, 1);
			cardGraphics.strokeRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
			// Copri il bordo superiore
			cardGraphics.lineStyle(1, 0xE0E0E0, 1);
			cardGraphics.beginPath();
			cardGraphics.moveTo(centerX - cardWidth / 2, y - cardHeight / 2);
			cardGraphics.lineTo(centerX + cardWidth / 2, y - cardHeight / 2);
			cardGraphics.strokePath();
		} else {
			// Righe normali: bordi quadrati
			cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
			cardGraphics.lineStyle(1, 0xE0E0E0, 1);
			cardGraphics.strokeRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
		}
		this.scrollContainer.add(cardGraphics);

		// Se è "IL TUO TURNO" e la sfida è ricevuta (non inviata), rendi la card cliccabile
		if (isYourTurn && !isSentChallenge) {
			// Crea una zona interattiva trasparente sopra la card
			var hitZone = this.add.zone(centerX, y, cardWidth, cardHeight);
			hitZone.setInteractive({ useHandCursor: true });
			hitZone.setDepth(50);
			this.scrollContainer.add(hitZone);

			var self = this;
			hitZone.on('pointerup', function() {
				// Ignora il click se stiamo scrollando
				if (self.isDragging) return;
				audio.playSound(self, 'button-click');
				self.handleChallengeClick(challenge);
			});

			// Evidenzia la card al hover
			hitZone.on('pointerover', function() {
				cardGraphics.clear();
				cardGraphics.fillStyle(0xE8F4FC, 1); // Colore di hover
				if (isLastRow) {
					cardGraphics.fillRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
					cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, borderRadius);
					cardGraphics.lineStyle(2, 0x1567a3, 1);
					cardGraphics.strokeRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
				} else {
					cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
					cardGraphics.lineStyle(2, 0x1567a3, 1);
					cardGraphics.strokeRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
				}
			});

			hitZone.on('pointerout', function() {
				cardGraphics.clear();
				cardGraphics.fillStyle(0xFFFFFF, 1);
				if (isLastRow) {
					cardGraphics.fillRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
					cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, borderRadius);
					cardGraphics.lineStyle(1, 0xE0E0E0, 1);
					cardGraphics.strokeRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
				} else {
					cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
					cardGraphics.lineStyle(1, 0xE0E0E0, 1);
					cardGraphics.strokeRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
				}
			});
		}

		// Avatar (placeholder - puoi usare l'immagine dell'utente se disponibile)
		const avatarSize = 50;
		const avatarX = centerX - cardWidth / 2 + padding + avatarSize / 2;
		const avatar = this.add.circle(avatarX, y, avatarSize / 2, 0xD3D3D3);
		this.scrollContainer.add(avatar);

		// Icona faccina winking (come nell'immagine)
		const faceIcon = this.add.text(avatarX, y, '😉', {
			fontSize: avatarSize * 0.7
		}).setOrigin(0.5);
		this.scrollContainer.add(faceIcon);

		// Determina l'altro utente: per sfide inviate è "friend", per sfide ricevute è "user"
		const otherUser = isSentChallenge ? challenge.friend : challenge.user;
		if (otherUser && otherUser.picture) {
			// Nota: per caricare immagini dinamiche serve un loader separato
			// Per ora usiamo un placeholder con faccina
		}

		// Nome utente
		var userName = 'In attesa dell\'avversario';
		if (otherUser && otherUser.nickname) {
			userName = otherUser.nickname;
		}
		const nameText = this.add.text(avatarX + avatarSize / 2 + 10, y - 18, userName.toUpperCase(), {
			fontSize: 34,
			fontFamily: 'Arial',
			fill: '#333333',
			fontStyle: 'bold'
		}).setOrigin(0, 0.5);
		this.scrollContainer.add(nameText);

		// Stato e punteggio (spostato più in basso del 15%)
		let statusY = y + (cardHeight * 0.15);
		let statusText;
		const challengeStatus = parseInt(challenge.status);
		const STATUS_ACCEPTED = 20;
		const ROUND_STATUS_CLOSED = 30;

		// Se entrambi hanno giocato (sfida ACCEPTED e challenged_round presente e chiuso)
		if (challengeStatus === STATUS_ACCEPTED && challenge.challenged_round) {
			const challengedRoundStatus = parseInt(challenge.challenged_round.status);
			if (challengedRoundStatus === ROUND_STATUS_CLOSED) {
				// Entrambi hanno giocato: mostra entrambi i punteggi con i nickname
				// Usa challenge_score se disponibile, altrimenti score
				const myScore = isSentChallenge
					? (challenge.round && (challenge.round.challenge_score !== null && challenge.round.challenge_score !== undefined ? challenge.round.challenge_score : challenge.round.score) || 0)
					: (challenge.challenged_round && (challenge.challenged_round.challenge_score !== null && challenge.challenged_round.challenge_score !== undefined ? challenge.challenged_round.challenge_score : challenge.challenged_round.score) || 0);
				const otherScore = isSentChallenge
					? (challenge.challenged_round && (challenge.challenged_round.challenge_score !== null && challenge.challenged_round.challenge_score !== undefined ? challenge.challenged_round.challenge_score : challenge.challenged_round.score) || 0)
					: (challenge.round && (challenge.round.challenge_score !== null && challenge.round.challenge_score !== undefined ? challenge.round.challenge_score : challenge.round.score) || 0);
				const myNickname = 'TU';
				const otherNickname = otherUser && otherUser.nickname ? otherUser.nickname : 'Avversario';
				statusText = this.add.text(avatarX + avatarSize / 2 + 10, statusY + 5, myNickname + ': ' + myScore + ' - ' + otherNickname + ': ' + otherScore, {
					fontSize: 31,
					fontFamily: 'Arial',
					fill: '#1567a3',
					fontStyle: 'bold'
				}).setOrigin(0, 0.5);
			} else {
				// L'altro non ha ancora completato
				if (isSentChallenge && challenge.round) {
					const score = challenge.round.score;
					if (score !== null && score !== undefined) {
						statusText = this.add.text(avatarX + avatarSize / 2 + 15, statusY, 'Il tuo punteggio: ' + score, {
							fontSize: 25,
							fontFamily: 'Arial',
							fill: '#1567a3',
							fontStyle: 'bold'
						}).setOrigin(0, 0.5);
					} else {
						statusText = this.add.text(avatarX + avatarSize / 2 + 15, statusY, 'In attesa...', {
							fontSize: 25,
							fontFamily: 'Arial',
							fill: '#666666'
						}).setOrigin(0, 0.5);
					}
				} else {
					statusText = this.add.text(avatarX + avatarSize / 2 + 15, statusY, 'In attesa...', {
						fontSize: 25,
						fontFamily: 'Arial',
						fill: '#666666'
					}).setOrigin(0, 0.5);
				}
			}
		} else if (isSentChallenge && challenge.round) {
			// Sfida inviata, mostra il mio punteggio
			const score = challenge.round.score;
			if (score !== null && score !== undefined) {
				statusText = this.add.text(avatarX + avatarSize / 2 + 10, statusY, 'Il tuo punteggio: ' + score, {
					fontSize: 25,
					fontFamily: 'Arial',
					fill: '#1567a3',
					fontStyle: 'bold'
				}).setOrigin(0, 0.5);
			} else {
				statusText = this.add.text(avatarX + avatarSize / 2 + 10, statusY, 'In attesa di approvazione', {
					fontSize: 25,
					fontFamily: 'Arial',
					fill: '#666666'
				}).setOrigin(0, 0.5);
			}
		} else {
			// Altrimenti mostra lo stato normale
			statusText = this.add.text(avatarX + avatarSize / 2 + 10, statusY, 'In attesa di approvazione', {
				fontSize: 25,
				fontFamily: 'Arial',
				fill: '#666666'
			}).setOrigin(0, 0.5);
		}
		this.scrollContainer.add(statusText);

		// Timestamp (posizionato a destra)
		const timeAgo = this.getTimeAgo(challenge.created_at);
		var timeString = '';
		if (timeAgo.includes('minute')) {
			var mins = parseInt(timeAgo.match(/\d+/)[0]);
			timeString = mins + (mins > 1 ? ' min' : ' min');
		} else if (timeAgo.includes('hour')) {
			var hours = parseInt(timeAgo.match(/\d+/)[0]);
			timeString = hours + (hours > 1 ? ' ore' : ' ora');
		} else if (timeAgo.includes('day')) {
			var days = parseInt(timeAgo.match(/\d+/)[0]);
			timeString = days + (days > 1 ? ' gg' : ' g');
		} else if (timeAgo === 'just now') {
			timeString = 'ora';
		} else {
			timeString = timeAgo;
		}

		var timestampX = centerX + cardWidth / 2 - 20;
		const timestampText = this.add.text(timestampX, y, timeString, {
			fontSize: 32,
			fontFamily: 'Arial',
			fill: '#999999'
		}).setOrigin(1, 0.5);
		this.scrollContainer.add(timestampText);

		return y + cardHeight;
	}

	createCompletedChallengeCard(centerX, y, challenge, isLastRow, isSentChallenge) {
		const cardWidth = this.sys.game.config.width * 0.9;
		const cardHeight = 78;
		const borderRadius = 10;

		// Background card
		const cardGraphics = this.add.graphics();
		cardGraphics.fillStyle(0xFFFFFF, 1);

		if (isLastRow) {
			cardGraphics.fillRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
			cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, borderRadius);
			cardGraphics.lineStyle(1, 0xE0E0E0, 1);
			cardGraphics.strokeRoundedRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight, borderRadius);
		} else {
			cardGraphics.fillRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
			cardGraphics.lineStyle(1, 0xE0E0E0, 1);
			cardGraphics.strokeRect(centerX - cardWidth / 2, y - cardHeight / 2, cardWidth, cardHeight);
		}
		this.scrollContainer.add(cardGraphics);

		// Determina chi sono io confrontando l'ID utente corrente
		// challenge.user è sempre il challenger (chi ha inviato la sfida)
		// challenge.friend è sempre il challenged (chi ha ricevuto la sfida)
		var currentUser = getUser();
		var currentUserId = currentUser ? parseInt(currentUser.id) : null;

		// Se il mio ID corrisponde a challenge.user.id, sono il challenger
		// Nelle sfide inviate (sentChallenges) non c'è challenge.user, quindi usiamo il round.user_id
		var iAmChallenger = false;
		if (challenge.user && challenge.user.id) {
			iAmChallenger = currentUserId === parseInt(challenge.user.id);
		} else if (challenge.round && challenge.round.user_id) {
			// Fallback: il round.user_id è l'ID del challenger
			iAmChallenger = currentUserId === parseInt(challenge.round.user_id);
		} else {
			// Ultimo fallback: usa il flag isSentChallenge
			iAmChallenger = isSentChallenge;
		}

		console.log('createCompletedChallengeCard - currentUserId:', currentUserId,
			'challenge.user.id:', challenge.user ? challenge.user.id : 'N/A',
			'challenge.round.user_id:', challenge.round ? challenge.round.user_id : 'N/A',
			'iAmChallenger:', iAmChallenger,
			'isSentChallenge:', isSentChallenge);
		console.log('createCompletedChallengeCard - PUNTEGGI:',
			'challenge.round.id:', challenge.round ? challenge.round.id : 'N/A',
			'challenge.round.challenge_score:', challenge.round ? challenge.round.challenge_score : 'N/A',
			'challenge.challenged_round.id:', challenge.challenged_round ? challenge.challenged_round.id : 'N/A',
			'challenge.challenged_round.challenge_score:', challenge.challenged_round ? challenge.challenged_round.challenge_score : 'N/A');

		// Determina i dati degli utenti
		var myNickname = 'TU';
		var otherUser = iAmChallenger ? challenge.friend : challenge.user;
		var otherNickname = otherUser && otherUser.nickname ? otherUser.nickname : 'Avversario';

		// Calcola punteggi - usa challenge_score se disponibile, altrimenti score
		// Se sono il challenger, il mio round è challenge.round
		// Se sono il challenged, il mio round è challenge.challenged_round
		var myScore = iAmChallenger
			? (challenge.round && (challenge.round.challenge_score !== null && challenge.round.challenge_score !== undefined ? challenge.round.challenge_score : challenge.round.score) || 0)
			: (challenge.challenged_round && (challenge.challenged_round.challenge_score !== null && challenge.challenged_round.challenge_score !== undefined ? challenge.challenged_round.challenge_score : challenge.challenged_round.score) || 0);
		var otherScore = iAmChallenger
			? (challenge.challenged_round && (challenge.challenged_round.challenge_score !== null && challenge.challenged_round.challenge_score !== undefined ? challenge.challenged_round.challenge_score : challenge.challenged_round.score) || 0)
			: (challenge.round && (challenge.round.challenge_score !== null && challenge.round.challenge_score !== undefined ? challenge.round.challenge_score : challenge.round.score) || 0);

		// Determina vincitore
		var iWon = myScore > otherScore;
		var isDraw = myScore === otherScore;

		// Layout (avatar ai lati della card)
		var avatarSize = 45;
		var avatarMargin = 55; // Margine dal bordo della card (aumentato per evitare che il nickname esca)
		var leftX = centerX - cardWidth / 2 + avatarMargin + avatarSize / 2;
		var rightX = centerX + cardWidth / 2 - avatarMargin - avatarSize / 2;
		var avatarY = y - 10;
		var nicknameY = avatarY + avatarSize / 2 + 10;

		// Avatar sinistro (io)
		var leftAvatar = this.add.graphics();
		leftAvatar.fillStyle(0x4A90D9, 1);
		leftAvatar.fillCircle(leftX, avatarY, avatarSize / 2);
		leftAvatar.lineStyle(3, 0xFFFFFF, 1);
		leftAvatar.strokeCircle(leftX, avatarY, avatarSize / 2);
		this.scrollContainer.add(leftAvatar);

		var leftFaceIcon = this.add.text(leftX, avatarY, '😊', {
			fontSize: avatarSize * 0.55
		}).setOrigin(0.5);
		this.scrollContainer.add(leftFaceIcon);

		// Nickname sinistro (io)
		var leftNickname = this.add.text(leftX, nicknameY, myNickname.toUpperCase(), {
			fontSize: 28,
			fontFamily: 'Arial',
			fill: '#333333',
			fontStyle: 'bold'
		}).setOrigin(0.5);
		this.scrollContainer.add(leftNickname);

		// Avatar destro (avversario)
		var rightAvatar = this.add.graphics();
		rightAvatar.fillStyle(0xE74C3C, 1);
		rightAvatar.fillCircle(rightX, avatarY, avatarSize / 2);
		rightAvatar.lineStyle(3, 0xFFFFFF, 1);
		rightAvatar.strokeCircle(rightX, avatarY, avatarSize / 2);
		this.scrollContainer.add(rightAvatar);

		var rightFaceIcon = this.add.text(rightX, avatarY, '😎', {
			fontSize: avatarSize * 0.55
		}).setOrigin(0.5);
		this.scrollContainer.add(rightFaceIcon);

		// Nickname destro (avversario)
		var rightNickname = this.add.text(rightX, nicknameY, otherNickname.toUpperCase(), {
			fontSize: 28,
			fontFamily: 'Arial',
			fill: '#333333',
			fontStyle: 'bold'
		}).setOrigin(0.5);
		this.scrollContainer.add(rightNickname);

		var barCenterX = (leftX + rightX) / 2; // Centro tra i due avatar

		// === BARRE PROPORZIONALI AL PUNTEGGIO (stile Ruzzle) ===
		var barPadding = 5;
		var barHeight = cardHeight - (barPadding * 2); // Barre occupano quasi tutta l'altezza della card
		var totalBarWidth = cardWidth * 0.50; // Larghezza totale disponibile per le due barre
		var barY = y; // Centro della card

		// Calcola proporzioni in base ai punteggi
		var totalScore = myScore + otherScore;
		var myRatio, otherRatio;
		if (totalScore === 0) {
			// Entrambi a zero: barre uguali
			myRatio = 0.5;
			otherRatio = 0.5;
		} else {
			myRatio = myScore / totalScore;
			otherRatio = otherScore / totalScore;
		}

		// Larghezza minima per mostrare il punteggio
		var minBarWidth = 40;
		var minBarWidthZero = 25;
		var myBarWidth = Math.max(myScore > 0 ? minBarWidth : minBarWidthZero, totalBarWidth * myRatio);
		var otherBarWidth = Math.max(otherScore > 0 ? minBarWidth : minBarWidthZero, totalBarWidth * otherRatio);

		// Ricalcola per assicurarsi che le barre entrino nello spazio
		var scaleFactor = totalBarWidth / (myBarWidth + otherBarWidth);
		myBarWidth = myBarWidth * scaleFactor;
		otherBarWidth = otherBarWidth * scaleFactor;

		// Colori barre
		var leftBarColor = isDraw ? 0x808080 : (iWon ? 0x4CAF50 : 0xE74C3C);
		var rightBarColor = isDraw ? 0x808080 : (iWon ? 0xE74C3C : 0x4CAF50);

		// Barra sinistra (il mio punteggio) - adiacente alla barra destra
		var leftBarStartX = barCenterX - totalBarWidth / 2;
		var leftBarGraphics = this.add.graphics();
		leftBarGraphics.fillStyle(leftBarColor, 1);
		leftBarGraphics.fillRoundedRect(
			leftBarStartX,
			barY - barHeight / 2,
			myBarWidth,
			barHeight,
			{ tl: 8, tr: 0, bl: 8, br: 0 }
		);
		this.scrollContainer.add(leftBarGraphics);

		// Punteggio sinistro (dentro la barra)
		var leftScoreText = this.add.text(leftBarStartX + myBarWidth / 2, barY, String(myScore), {
			fontSize: 48,
			fontFamily: 'Luckiest Guy',
			fill: '#FFFFFF'
		}).setOrigin(0.5);
		this.scrollContainer.add(leftScoreText);

		// Barra destra (punteggio avversario) - inizia subito dopo la barra sinistra
		var rightBarStartX = leftBarStartX + myBarWidth;
		var rightBarGraphics = this.add.graphics();
		rightBarGraphics.fillStyle(rightBarColor, 1);
		rightBarGraphics.fillRoundedRect(
			rightBarStartX,
			barY - barHeight / 2,
			otherBarWidth,
			barHeight,
			{ tl: 0, tr: 8, bl: 0, br: 8 }
		);
		this.scrollContainer.add(rightBarGraphics);

		// Punteggio destro (dentro la barra)
		var rightScoreText = this.add.text(rightBarStartX + otherBarWidth / 2, barY, String(otherScore), {
			fontSize: 48,
			fontFamily: 'Luckiest Guy',
			fill: '#FFFFFF'
		}).setOrigin(0.5);
		this.scrollContainer.add(rightScoreText);

		// === AREA CLICCABILE PER TUTTA LA CARD ===
		var cardHitArea = this.add.rectangle(
			centerX,
			y,
			cardWidth,
			cardHeight,
			0x000000,
			0 // Trasparente
		);
		cardHitArea.setDepth(100);
		cardHitArea.setInteractive({ useHandCursor: true });
		this.scrollContainer.add(cardHitArea);

		var self = this;
		cardHitArea.on('pointerup', function(pointer, localX, localY, event) {
			// Ignora il click se stiamo scrollando
			if (self.isDragging) return;
			if (event) {
				event.stopPropagation();
			}
			console.log('Opening challenge detail:', challenge.id);
			console.log('isSentChallenge:', isSentChallenge);

			self.time.delayedCall(50, function() {
				console.log('Switching to ChallengeDetail scene...');
				if (self.scene.isActive('MainMenu') || self.scene.isPaused('MainMenu')) {
					self.scene.stop('MainMenu');
				}
				// Chiama l'API per ottenere i punteggi calcolati della sfida
				axios.get('/api/invites/' + challenge.id + '/result')
					.then(function(response) {
						console.log('Dati sfida caricati:', response.data);
						self.scene.start('ChallengeDetail', {
							challenge: response.data.invite,
							result: response.data.result,
							isSentChallenge: isSentChallenge
						});
					})
					.catch(function(error) {
						console.error('Errore caricamento dati sfida:', error);
						// Fallback: apri senza i punteggi calcolati
						self.scene.start('ChallengeDetail', {
							challenge: challenge,
							isSentChallenge: isSentChallenge
						});
					});
			});
		});

		// Effetto hover sulla card
		cardHitArea.on('pointerover', function() {
			cardGraphics.setAlpha(0.9);
		});
		cardHitArea.on('pointerout', function() {
			cardGraphics.setAlpha(1);
		});

		return y + cardHeight;
	}

	getTimeAgo(timestamp) {
		if (!timestamp) return 'unknown';

		// timestamp è Unix timestamp in secondi
		const now = Math.floor(Date.now() / 1000);
		const created = parseInt(timestamp);
		const diffSecs = now - created;
		const diffMins = Math.floor(diffSecs / 60);
		const diffHours = Math.floor(diffSecs / 3600);
		const diffDays = Math.floor(diffSecs / 86400);

		if (diffMins < 1) return 'just now';
		if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
		if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
		return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
	}

	showError(message) {
		if (this.loadingText) {
			this.loadingText.destroy();
		}
		this.loadingText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			message,
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FF0000'}
		).setOrigin(0.5);
	}

	/**
	 * Gestisce il click su una sfida ricevuta nella sezione "IL TUO TURNO"
	 * - Se STATUS_WAITING: accetta la sfida e avvia il gioco
	 * - Se STATUS_ACCEPTED con round in corso: riprende il gioco
	 * @param {Object} challenge L'oggetto sfida
	 */
	handleChallengeClick(challenge) {
		var self = this;
		var STATUS_WAITING = 10;
		var STATUS_ACCEPTED = 20;
		var ROUND_STATUS_RUNNING = 20;

		var challengeStatus = parseInt(challenge.status);

		// Mostra un indicatore di caricamento
		var centerX = this.sys.game.config.width / 2;
		var centerY = this.sys.game.config.height / 2;
		var loadingOverlay = this.add.rectangle(centerX, centerY, this.sys.game.config.width, this.sys.game.config.height, 0x000000, 0.5);
		loadingOverlay.setDepth(200);
		var loadingTextChallenge = this.add.text(centerX, centerY, 'Caricamento...', {
			fontSize: 32,
			fontFamily: 'Luckiest Guy',
			fill: '#FFFFFF'
		}).setOrigin(0.5);
		loadingTextChallenge.setDepth(201);

		if (challengeStatus === STATUS_WAITING) {
			// La sfida è in attesa di accettazione: chiama l'API per accettarla
			console.log('Accetto sfida:', challenge.id);

			axios.post('/api/invites/' + challenge.id + '/accept')
				.then(function(response) {
					var data = response.data;
					console.log('Risposta accept:', data);

					// Rimuovi overlay
					loadingOverlay.destroy();
					loadingTextChallenge.destroy();

					if (data.immediate_result === true) {
						// L'utente ha già giocato questo dict_id: mostra il dettaglio sfida
						console.log('Risultato immediato, vado a ChallengeDetail');
						self.scene.start('ChallengeDetail', {
							challenge: data.invite,
							result: data.result, // Punteggi calcolati dalla API
							isSentChallenge: false
						});
					} else {
						// L'utente deve giocare: un nuovo round è stato creato
						console.log('Round creato, vado a giocare:', data.round);
						// Salva l'info della sfida nel runtime per usarla nel game-over
						runtime.currentChallenge = challenge;
						self.startGameWithRound(data.round, challenge);
					}
				})
				.catch(function(error) {
					console.error('Errore accettazione sfida:', error);
					loadingOverlay.destroy();
					loadingTextChallenge.destroy();

					var errorMessage = 'Errore nell\'accettazione della sfida';
					if (error.response && error.response.data && error.response.data.message) {
						errorMessage = error.response.data.message;
					}
					self.showError(errorMessage);
				});

		} else if (challengeStatus === STATUS_ACCEPTED) {
			// La sfida è già stata accettata: verifica se c'è un round in corso
			if (challenge.challenged_round) {
				var roundStatus = parseInt(challenge.challenged_round.status);
				if (roundStatus === ROUND_STATUS_RUNNING) {
					// Il round è in corso: riprende il gioco
					console.log('Round in corso, riprendo:', challenge.challenged_round);
					loadingOverlay.destroy();
					loadingTextChallenge.destroy();
					// Salva l'info della sfida nel runtime per usarla nel game-over
					runtime.currentChallenge = challenge;
					self.startGameWithRound(challenge.challenged_round, challenge);
				} else {
					// Il round non è in corso (potrebbe essere già chiuso)
					// In questo caso chiama l'API per ottenere i punteggi calcolati
					console.log('Round già completato, carico risultati sfida');
					axios.get('/api/invites/' + challenge.id + '/result')
						.then(function(response) {
							console.log('Dati sfida caricati:', response.data);
							loadingOverlay.destroy();
							loadingTextChallenge.destroy();
							self.scene.start('ChallengeDetail', {
								challenge: response.data.invite,
								result: response.data.result,
								isSentChallenge: false
							});
						})
						.catch(function(error) {
							console.error('Errore caricamento dati sfida:', error);
							loadingOverlay.destroy();
							loadingTextChallenge.destroy();
							// Fallback: apri senza i punteggi calcolati
							self.scene.start('ChallengeDetail', {
								challenge: challenge,
								isSentChallenge: false
							});
						});
				}
			} else {
				// Non c'è challenged_round, quindi devo crearlo chiamando l'API
				// Questo caso non dovrebbe succedere normalmente, ma lo gestiamo
				console.log('Sfida accettata ma senza round, creo round via Boot');
				loadingOverlay.destroy();
				loadingTextChallenge.destroy();
				// Vai a Boot con invite_id per creare il round
				self.scene.start('Boot', { invite_id: challenge.id });
			}
		} else {
			// Stato non gestito
			console.warn('Stato sfida non gestito:', challengeStatus);
			loadingOverlay.destroy();
			loadingTextChallenge.destroy();
		}
	}

	/**
	 * Avvia il gioco con un round specifico (creato dalla sfida)
	 * @param {Object} round Il round da giocare
	 * @param {Object} challenge La sfida associata
	 */
	startGameWithRound(round, challenge) {
		var self = this;

		// Ottieni il game_mode_id dal round del challenger
		var gameModeId = null;
		if (challenge.round && challenge.round.game_id) {
			// Devo ottenere il game_mode_id dal game
			// Per semplicità, uso quello salvato nel localStorage o un default
			gameModeId = getGameModeId();
		}

		if (!gameModeId) {
			// Se non ho un game_mode_id, uso un default o vado a Boot
			console.log('Nessun game_mode_id, vado a Boot');
			this.scene.start('Boot');
			return;
		}

		// Carica i dati del gioco tramite l'API start
		// L'API troverà il round RUNNING esistente e lo restituirà
		axios.get('/api/rounds/start?game_mode_id=' + gameModeId)
			.then(function(response) {
				// Imposta i dati nel runtime
				runtime.user = response.data.user;
				runtime.game = response.data.game;
				runtime.mode = response.data.game.modes ? response.data.game.modes.find(function(m) {
					return m.id === parseInt(gameModeId);
				}) : null;
				runtime.dict = response.data.dict;
				runtime.skills = response.data.skills;
				runtime.superpowers = response.data.superpowers;
				runtime.gamifications = response.data.gamifications;
				runtime.statuses = response.data.statuses;
				runtime.constants = response.data.constants;
				runtime.round = response.data.round;
				runtime.ranking = response.data.ranking;

				// Avvia la scena di gioco
				self.scene.start('Game');
			})
			.catch(function(error) {
				console.error('Errore caricamento gioco:', error);
				var errorMessage = 'Errore nel caricamento del gioco';
				if (error.response && error.response.data && error.response.data.message) {
					errorMessage = error.response.data.message;
				}
				self.showError(errorMessage);
			});
	}
}
